import { Fragment, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import SubHeader from "../../components/global/SubHeader";
import CustomPopUp from "../../components/global/CustomPopup";
import {
  useCommunicationPreferencesOverview,
  useDeliveryHistory,
  useDeliveryHistoryPreview,
} from "../../services/utils/custom hooks/Communication/useCommunicationPreferences";
import NotificationSettingsTableLoadingIndicator from "../../components/global/LoadingIndicators/NotificationsSettingsTableLoadingIndicator";
import {
  COMMUNICATION_SELECT_CHEVRON_STYLE,
  COMMUNICATION_SELECT_CLASS,
  DeliveryHistoryChannel,
  DeliveryHistoryStatus,
} from "../../constants/communicationConstants";

function channelLabel(channel: DeliveryHistoryChannel | string): string {
  switch (channel) {
    case DeliveryHistoryChannel.EMAIL:
      return "Email";
    case DeliveryHistoryChannel.SMS:
      return "SMS";
    case DeliveryHistoryChannel.FAX:
      return "Fax";
    default:
      return String(channel);
  }
}

function statusLabel(status: DeliveryHistoryStatus | string): string {
  switch (status) {
    case DeliveryHistoryStatus.SENT:
      return "Sent";
    case DeliveryHistoryStatus.FAILED:
      return "Failed";
    case DeliveryHistoryStatus.SUPPRESSED:
      return "Suppressed";
    default:
      return String(status);
  }
}

function DeliveryHistoryPage(): JSX.Element {
  const history = useHistory();
  const { data: overview, isLoading: overviewLoading } =
    useCommunicationPreferencesOverview();
  const [organizationFilter, setOrganizationFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState<"all" | DeliveryHistoryChannel>(
    "all"
  );
  const [statusFilter, setStatusFilter] = useState<"all" | DeliveryHistoryStatus>(
    "all"
  );
  const [previewId, setPreviewId] = useState<number | null>(null);

  const organizations = useMemo(
    () =>
      (overview?.organizations ?? []).map((org) => ({
        id: String(org.organizationId),
        name: org.organizationName,
      })),
    [overview]
  );

  const filters = useMemo(
    () => ({
      // Only include organizationId when a specific org is selected.
      organizationId:
        organizationFilter === "all" ? undefined : organizationFilter,
      channel: channelFilter,
      status: statusFilter,
      limit: 50,
    }),
    [organizationFilter, channelFilter, statusFilter]
  );

  const { data: items = [], isLoading: historyLoading, isFetching } =
    useDeliveryHistory(filters, true);
  const { data: previewItem, isLoading: previewLoading } =
    useDeliveryHistoryPreview(previewId);

  const isLoading = overviewLoading || historyLoading || isFetching;

  return (
    <Fragment>
      <SubHeader
        title="Communication log"
        hideSubHeaderSelectors
        reDirect={() =>
          history.push("/access/notifications/settings/my-preferences")
        }
      />
      <div className="px-6 py-8 pb-12">
        <p className="text-sm text-textSecondaryGray mb-6">
          A concise record of notifications sent to you. Sent does not mean the
          message was read.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-royalBlue mb-1">
              Organization
            </label>
            <select
              value={organizationFilter}
              onChange={(e) => setOrganizationFilter(e.target.value)}
              className={COMMUNICATION_SELECT_CLASS}
              style={COMMUNICATION_SELECT_CHEVRON_STYLE}
            >
              <option value="all">All organizations</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-royalBlue mb-1">
              Channel
            </label>
            <select
              value={channelFilter}
              onChange={(e) =>
                setChannelFilter(
                  e.target.value as "all" | DeliveryHistoryChannel
                )
              }
              className={COMMUNICATION_SELECT_CLASS}
              style={COMMUNICATION_SELECT_CHEVRON_STYLE}
            >
              <option value="all">All</option>
              <option value={DeliveryHistoryChannel.EMAIL}>Email</option>
              <option value={DeliveryHistoryChannel.SMS}>SMS</option>
              <option value={DeliveryHistoryChannel.FAX}>Fax</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-royalBlue mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as "all" | DeliveryHistoryStatus
                )
              }
              className={COMMUNICATION_SELECT_CLASS}
              style={COMMUNICATION_SELECT_CHEVRON_STYLE}
            >
              <option value="all">All</option>
              <option value={DeliveryHistoryStatus.SENT}>Sent</option>
              <option value={DeliveryHistoryStatus.FAILED}>Failed</option>
              <option value={DeliveryHistoryStatus.SUPPRESSED}>
                Suppressed
              </option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <NotificationSettingsTableLoadingIndicator />
        ) : (
          <div className="overflow-auto border border-dropDownBorder rounded-md">
            <table className="table-auto w-full bg-white">
              <thead className="bg-sideNavGray">
                <tr className="text-left text-sm text-royalBlue">
                  <th className="px-4 py-3 font-semibold">Notification</th>
                  <th className="px-4 py-3 font-semibold">Organization</th>
                  <th className="px-4 py-3 font-semibold">Channel</th>
                  <th className="px-4 py-3 font-semibold">Destination</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">View message</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-sm text-textSecondaryGray text-center"
                    >
                      No communication log found.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-t border-dropDownBorder text-sm text-royalBlue"
                    >
                      <td className="px-4 py-3">{item.notification}</td>
                      <td className="px-4 py-3">{item.organizationName}</td>
                      <td className="px-4 py-3">
                        {channelLabel(item.channel)}
                      </td>
                      <td className="px-4 py-3">{item.destination}</td>
                      <td className="px-4 py-3">{statusLabel(item.status)}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="text-primary font-semibold"
                          onClick={() => setPreviewId(item.id)}
                        >
                          View message
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {previewId !== null && (
        <CustomPopUp
          popUpTitle={previewItem?.notification ?? "Message preview"}
          hidePopUp={() => setPreviewId(null)}
          warningPopupNotificationSettings
        >
          {previewLoading || !previewItem ? (
            <NotificationSettingsTableLoadingIndicator />
          ) : (
            <div className="space-y-3 text-sm text-royalBlue">
              <p>
                <span className="font-semibold">Organization:</span>{" "}
                {previewItem.organizationName}
              </p>
              <p>
                <span className="font-semibold">Channel:</span>{" "}
                {channelLabel(previewItem.channel)}
              </p>
              <p>
                <span className="font-semibold">Destination:</span>{" "}
                {previewItem.destination}
              </p>
              <p>
                <span className="font-semibold">Status:</span>{" "}
                {statusLabel(previewItem.status)}
              </p>
              <div className="pt-3 border-t border-dropDownBorder">
                <p className="font-semibold mb-2">Message</p>
                <div
                  className="text-textSecondaryGray"
                  dangerouslySetInnerHTML={{
                    __html: previewItem.messagePreview ?? "",
                  }}
                />
              </div>
            </div>
          )}
        </CustomPopUp>
      )}
    </Fragment>
  );
}

export default DeliveryHistoryPage;
